<template>
  <div class="h-screen flex md:flex-row bg-zinc-950 overflow-hidden font-light selection:bg-white selection:text-black">

    <div class="bg-zinc-950 flex flex-col justify-center items-center w-full md:w-1/2 p-8 sm:p-12 md:p-16 overflow-y-auto no-scrollbar border-r border-zinc-900/60 relative">
      <div class="absolute top-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-zinc-900/20 rounded-full blur-[100px] pointer-events-none"></div>

      <div class="w-full max-w-md mb-8 text-left">
        <span class="text-xs font-serif italic text-[#D5CCBC] tracking-wide">BookMySalon PRO</span>
      </div>

      <!-- LOGIN FORM -->
      <form v-if="authMode === 'login'" @submit.prevent="handleLogin" class="w-full max-w-md space-y-6 text-left relative z-10">
        <div class="space-y-1">
          <h2 class="text-xl font-medium text-white tracking-tight">Réseau d'Abonnés</h2>
          <p class="text-xs text-zinc-500 font-light">Accédez à votre espace de gestion et à vos indicateurs de performance.</p>
        </div>

        <div class="space-y-4 pt-2">
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium" for="email">adresse Email</label>
            <input v-model="loginData.email" id="email" placeholder="adresse@BookMySalon-studio.com" required type="email"
              class="w-full px-4 py-3 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900/80 text-sm tracking-wide transition-all duration-300" />
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium" for="password">Mot de Passe</label>
            <div class="relative">
              <input v-model="loginData.password" :type="passwordVisible ? 'text' : 'password'" id="password" placeholder="••••••••••••" required
                class="w-full pl-4 pr-12 py-3 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900/80 text-sm tracking-wide transition-all duration-300" />
              <button type="button" @click="passwordVisible = !passwordVisible" class="absolute inset-y-0 right-4 flex items-center text-zinc-600 hover:text-zinc-400 transition-colors focus:outline-none">
                <span class="text-xs tracking-wider uppercase font-medium">{{ passwordVisible ? 'Masquer' : 'Voir' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end">
          <button type="button" class="text-xs text-[#D5CCBC] hover:text-white transition-colors tracking-wide focus:outline-none" @click="authMode = 'reset'">
            Identifiants perdus ?
          </button>
        </div>

        <div class="pt-2">
          <button
            :disabled="isLoading"
            class="w-full py-3.5 rounded-xl bg-white hover:bg-[#D5CCBC] text-black font-semibold text-xs uppercase tracking-wider transition-all duration-300 shadow-xl shadow-white/5 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
            type="submit">
            {{ isLoading ? 'Connexion...' : 'Se connecter' }}
          </button>
        </div>

        <div class="flex items-center pt-6">
          <hr class="flex-grow border-zinc-900"/>
          <span class="mx-4 text-[9px] tracking-widest text-zinc-600 font-medium uppercase">Nouvelle Structure</span>
          <hr class="flex-grow border-zinc-900"/>
        </div>

        <button class="w-full py-3.5 rounded-xl bg-zinc-900/30 hover:bg-zinc-900 text-zinc-300 hover:text-white font-medium text-xs uppercase tracking-wider border border-zinc-800/80 transition-all duration-300" type="button" @click="startRegistration">
          Créer un profil d'établissement
        </button>
      </form>

<div v-else-if="authMode === 'register'" class="w-full max-w-lg text-left my-auto relative z-10 space-y-6 select-none">
  <!-- En-tête -->
  <div class="space-y-1">
    <h2 class="text-2xl font-bold text-white tracking-tight">Ouverture de Compte</h2>
    <p class="text-sm text-zinc-400 font-light leading-relaxed">Rejoignez l'infrastructure de réservation esthétique haute performance.</p>
  </div>

  <!-- Stepper -->
  <div class="flex flex-col space-y-4 py-1">
    <div class="flex items-center justify-between">
      <div v-for="step in totalSteps" :key="step" class="flex items-center flex-1 last:flex-none">
        <div class="flex flex-col items-center relative">
          <div
            :class="[
              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ring-4 ring-black z-10',
              currentStep >= step
                ? 'bg-white text-zinc-950 shadow-xl shadow-white/10'
                : 'bg-zinc-900 text-zinc-500 border border-zinc-800'
            ]">
            <span v-if="currentStep > step" class="text-xs font-bold">✓</span>
            <span v-else>{{ step }}</span>
          </div>
          <span
            :class="[
              'absolute top-10 whitespace-nowrap text-[9px] uppercase tracking-widest transition-all duration-300 font-bold',
              currentStep >= step ? 'text-zinc-200' : 'text-zinc-600'
            ]">
            {{ stepLabels[step - 1] }}
          </span>
        </div>
        <div
          v-if="step < totalSteps"
          :class="[
            'h-[2px] flex-grow mx-4 transition-all duration-500 rounded-full',
            currentStep > step ? 'bg-white' : 'bg-zinc-800'
          ]" />
      </div>
    </div>
    <div class="h-2"></div>
  </div>

  <!-- Formulaire principal (Sans Scroll) -->
  <form @submit.prevent="currentStep < totalSteps ? nextStep() : handleRegister()" class="space-y-4">

    <!-- ÉTAPE 1 : PROPRIÉTAIRE -->
    <transition name="fade" mode="out-in">
      <div v-if="currentStep === 1" key="step1" class="space-y-4 pt-1">
        <p class="text-[10px] uppercase tracking-widest text-zinc-400 font-bold border-b border-zinc-900 pb-2">Informations personnelles du propriétaire</p>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-1.5">
            <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="nom">Nom</label>
            <input v-model="registerData.nom" id="nom" placeholder="Ex: Dupont" required type="text"
              class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
          <div class="space-y-1.5">
            <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="prenom">Prénom</label>
            <input v-model="registerData.prenom" id="prenom" placeholder="Ex: Jean" required type="text"
              class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
        </div>

        <div class="space-y-1.5">
          <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="email-register-step1">Email du propriétaire</label>
          <input v-model="registerData.email" id="email-register-step1" placeholder="jean.dupont@gmail.com" required type="email"
            class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
        </div>

        <div class="grid grid-cols-2 gap-4 items-start">
          <div class="space-y-1.5">
            <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="password-register">Mot de passe</label>
            <input v-model="registerData.password" id="password-register" placeholder="••••••••" required type="password"
              class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
            
            <div v-if="registerData.password" class="flex gap-1 pt-1.5">
              <div v-for="i in 4" :key="i" :class="['h-1 flex-1 rounded-full transition-all duration-300', i <= passwordStrength.level ? (passwordStrength.level === 1 ? 'bg-red-500' : passwordStrength.level === 2 ? 'bg-orange-500' : passwordStrength.level === 3 ? 'bg-yellow-500' : 'bg-emerald-500') : 'bg-zinc-800']" />
            </div>
          </div>

          <div class="space-y-1.5">
            <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="password-confirm">Confirmer le mot de passe</label>
            <input v-model="registerData.passwordConfirm" id="password-confirm" placeholder="••••••••" required type="password"
              class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
        </div>

        <div v-if="registerData.password" class="grid grid-cols-2 gap-x-3 gap-y-1 bg-zinc-950/40 border border-zinc-900 rounded-xl p-2 text-[10px]">
          <div :class="['flex items-center gap-1.5 transition-colors', passwordStrength.hasMinLength ? 'text-emerald-400' : 'text-zinc-500']">
            <span>{{ passwordStrength.hasMinLength ? '✓' : '○' }}</span> 8 caractères min.
          </div>
          <div :class="['flex items-center gap-1.5 transition-colors', passwordStrength.hasUpperCase ? 'text-emerald-400' : 'text-zinc-500']">
            <span>{{ passwordStrength.hasUpperCase ? '✓' : '○' }}</span> Une majuscule
          </div>
          <div :class="['flex items-center gap-1.5 transition-colors', passwordStrength.hasNumber ? 'text-emerald-400' : 'text-zinc-500']">
            <span>{{ passwordStrength.hasNumber ? '✓' : '○' }}</span> Un chiffre
          </div>
          <div :class="['flex items-center gap-1.5 transition-colors', passwordStrength.hasSpecialChar ? 'text-emerald-400' : 'text-zinc-500']">
            <span>{{ passwordStrength.hasSpecialChar ? '✓' : '○' }}</span> Un char. spécial
          </div>
        </div>
      </div>
    </transition>

    <!-- ÉTAPE 2 : ÉTABLISSEMENT -->
    <transition name="fade" mode="out-in">
      <div v-if="currentStep === 2" key="step2" class="space-y-3 pt-1">
        <p class="text-[10px] uppercase tracking-widest text-zinc-400 font-bold border-b border-zinc-900 pb-2">Informations de l'Établissement</p>

        <div class="space-y-1.5">
          <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="nom_societe">Nom commercial de l'Établissement</label>
          <div class="relative" ref="prospectInputContainer">
            <input v-model="registerData.nom_societe" @input="handleProspectInput" @focus="() => { updateProspectDropdownPosition(); if (registerData.nom_societe && registerData.nom_societe.length >= 2 && prospectSuggestions && prospectSuggestions.length > 0) { showProspectSuggestions = true } }" id="nom_societe" ref="prospectInput" placeholder="Maison d'Esthétique Paris" required type="text" autocomplete="off"
              class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
            <div v-if="selectedProspect" class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-500/40 px-2 py-0.5 rounded-md">
              <span class="text-[9px] text-emerald-300 font-semibold uppercase tracking-wider">Fiche trouvée</span>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-1.5">
            <div class="flex justify-between items-center">
              <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="siret">SIRET</label>
              <span :class="['text-[9px] font-mono tracking-tight', registerData.siret?.replace(/\s/g, '').length === 14 ? 'text-emerald-400' : 'text-zinc-600']">{{ registerData.siret?.replace(/\s/g, '').length || 0 }}/14</span>
            </div>
            <input v-model="registerData.siret" id="siret" placeholder="12345678900012" maxlength="14" required type="text" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white font-mono placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
          <div class="space-y-1.5">
            <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="telephone_firm">Téléphone Pro</label>
            <input v-model="registerData.telephone_firm" id="telephone_firm" placeholder="01 00 00 00 00" required type="tel" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
        </div>

        <div class="space-y-1.5">
          <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="adresse">Adresse Complète</label>
          <!-- Résout l'erreur 'addressInputContainer' is assigned a value but never used -->
          <div class="relative" ref="addressInputContainer">
            <input v-model="registerData.adresse" @input="handleAddressInput" @focus="() => { updateDropdownPosition(); if (registerData.adresse && registerData.adresse.length >= 3 && addressSuggestions && addressSuggestions.length > 0) { showAddressSuggestions = true } }" id="adresse" placeholder="12 Rue de la Paix, 75001 Paris" required type="text" autocomplete="off" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
        </div>

        <div class="space-y-2 pt-1">
          <label class="flex items-center gap-3 cursor-pointer group select-none">
            <input type="checkbox" v-model="registerData.sameAsOwnerEmail" class="h-4 w-4 rounded border-zinc-800 bg-zinc-950 text-white accent-white cursor-pointer focus:ring-0 focus:ring-offset-0" />
            <span class="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors">Utiliser l'adresse email du propriétaire</span>
          </label>
          <div v-if="!registerData.sameAsOwnerEmail" class="space-y-1.5 transition-all duration-200 local-fade-in">
            <input v-model="registerData.email_firm" id="email_firm" placeholder="contact@salon.com" :required="!registerData.sameAsOwnerEmail" type="email" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-600 focus:outline-none focus:border-white focus:ring-1 focus:ring-white text-sm transition-all duration-200" />
          </div>
        </div>
      </div>
    </transition>

    <!-- ÉTAPE 3 : CONFIGURATION & TARIFS -->
    <transition name="fade" mode="out-in">
      <div v-if="currentStep === 3" key="step3" class="space-y-4 pt-1">
        <p class="text-[10px] uppercase tracking-widest text-zinc-400 font-bold border-b border-zinc-900 pb-2">Configuration & Tarification du Compte</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <!-- Colonne Gauche -->
          <div class="space-y-3">
            <div class="space-y-1">
              <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="nombre_employes">Nombre de collaborateurs</label>
              <input v-model.number="registerData.nombre_employes" id="nombre_employes" placeholder="Ex: 5" required type="number" min="1" @input="updateTypeCompte"
                class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white text-sm transition-all duration-200" />
            </div>

            <div class="space-y-1">
              <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold" for="referralCode">Code parrainage (Optionnel)</label>
              <!-- Résout l'erreur 'handleReferralCodeInput' et 'validateReferralCode' assigned but never used -->
              <input v-model="registerData.referralCode" @input="handleReferralCodeInput" @blur="validateReferralCode" id="referralCode" placeholder="Code" type="text" maxlength="8"
                class="w-full px-4 py-2 rounded-xl border bg-zinc-950 text-white text-sm uppercase transition-all duration-200"
                :class="referralValidation.status === 'valid' ? 'border-emerald-500 bg-emerald-950/20' : referralValidation.status === 'invalid' ? 'border-red-500 bg-red-950/20' : 'border-zinc-800'" />
            </div>

            <div class="space-y-1.5">
              <label class="block text-[11px] uppercase tracking-wider text-zinc-300 font-semibold">Type de Tarification</label>
              <div class="grid grid-cols-2 gap-2">
                <button type="button" @click="registerData.type_tarification = 'flexible'" :class="['py-2 px-3 rounded-xl border text-xs font-bold transition-all duration-200', registerData.type_tarification === 'flexible' ? 'bg-white text-zinc-950 border-white' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200']">Mensuel</button>
                <button type="button" @click="registerData.type_tarification = 'lifetime'" :class="['py-2 px-3 rounded-xl border text-xs font-bold transition-all duration-200 relative overflow-hidden', registerData.type_tarification === 'lifetime' ? 'bg-zinc-100 text-zinc-950 border-white' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200']">
                  <span class="relative z-10">À VIE 🔥</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Colonne Droite -->
          <div class="space-y-3">
            <div class="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3.5 space-y-2.5 backdrop-blur-md shadow-xl shadow-black/40">
              <div class="flex items-center justify-between text-[11px]">
                <span class="text-zinc-400 font-medium">Plan :</span>
                <span class="font-bold text-zinc-200 px-2 py-0.5 bg-zinc-800 rounded text-[9px] uppercase tracking-wider">{{ selectedPlanName }}</span>
              </div>

              <div class="flex items-baseline justify-between pt-2 border-t border-zinc-800/60">
                <span class="text-xs text-zinc-400 font-medium">Montant global</span>
                <span class="text-xl font-bold tracking-tight text-white">
                  {{ calculatedPrice.toFixed(2) }}€
                  <span class="text-[10px] font-normal text-zinc-500 lowercase">{{ registerData.type_tarification === 'flexible' ? '/mois' : 'unique' }}</span>
                </span>
              </div>

              <p class="text-[10px] text-zinc-400 leading-normal font-light">
                {{ registerData.type_tarification === 'flexible' ? 'Abonnement mensuel sans engagement. Résiliation en un clic.' : 'Accès illimité à vie, aucune redevance future.' }}
              </p>
            </div>

            <div class="bg-amber-950/10 border border-amber-900/30 rounded-xl p-2">
              <p class="text-[9px] text-amber-300 leading-tight">
                ⚠️ Offre <strong class="text-amber-200">À Vie</strong> limitée à 100 places. Le choix est définitif après validation.
              </p>
            </div>
          </div>
        </div>

        <!-- Case CGU -->
        <label class="flex items-start gap-3 pt-2 cursor-pointer group select-none border-t border-zinc-900">
          <input v-model="registerData.acceptCGU" id="cgu" required type="checkbox" class="mt-0.5 h-4 w-4 rounded border-zinc-800 bg-zinc-950 text-white accent-white transition-all cursor-pointer" />
          <span class="text-[11px] text-zinc-400 font-normal leading-normal group-hover:text-zinc-300 transition-colors">
            J'accepte les <a href="/terms" class="text-zinc-200 underline underline-offset-2 hover:text-white font-semibold">Conditions Générales d'Utilisation</a>.
          </span>
        </label>
      </div>
    </transition>

    <button type="submit" class="absolute -absolute-x-full opacity-0 pointer-events-none" tabindex="-1"></button>
  </form>

  <!-- Navigation -->
  <div class="flex gap-3 pt-2 border-t border-zinc-900">
    <button v-if="currentStep > 1" type="button" @click="prevStep" class="flex-1 py-3 rounded-xl bg-zinc-950 hover:bg-zinc-900 text-zinc-300 hover:text-white text-xs uppercase tracking-widest font-bold border border-zinc-800 transition-all duration-200 active:scale-[0.98]">
      Précédent
    </button>
    <button v-if="currentStep < totalSteps" type="button" @click="nextStep" class="flex-1 py-3 rounded-xl bg-white hover:bg-zinc-100 text-zinc-950 font-bold text-xs uppercase tracking-widest transition-all duration-200 shadow-xl shadow-white/5 active:scale-[0.98]">
      Suivant
    </button>
    <button v-else type="button" @click.prevent="handleRegister" :disabled="isLoading || !registerData.acceptCGU" class="flex-1 py-3 rounded-xl bg-white hover:bg-zinc-100 text-zinc-950 font-bold text-xs uppercase tracking-widest transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed shadow-xl shadow-white/5 active:scale-[0.98]">
      {{ isLoading ? 'Création...' : 'Confirmer' }}
    </button>
  </div>

  <!-- Lien de retour -->
  <div class="text-center pt-1">
    <button class="text-xs text-zinc-500 hover:text-zinc-300 transition-colors duration-200 underline underline-offset-4" type="button" @click="cancelRegistration">
      Retourner à la connexion
    </button>
  </div>
</div>

      <!-- RESET PASSWORD FORM -->
      <form v-else-if="authMode === 'reset'" @submit.prevent="handleResetPassword" class="w-full max-w-md space-y-6 text-left relative z-10">
        <div class="space-y-1">
          <h2 class="text-xl font-medium text-white tracking-tight">Récupération d'accès</h2>
          <p class="text-xs text-zinc-500 font-light">Un lien de réinitialisation sécurisé vous sera transmis instantanément.</p>
        </div>

        <div class="space-y-1.5 pt-4">
          <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium" for="email-reset">Adresse de secours</label>
          <input v-model="resetPasswordData.email" id="email-reset" placeholder="adresse@BookMySalon-studio.com" required type="email"
            class="w-full px-4 py-3 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900/80 text-sm tracking-wide transition-all duration-300" />
        </div>

        <div class="space-y-3 pt-2">
          <button
            :disabled="isLoading"
            class="w-full py-3.5 rounded-xl bg-white hover:bg-[#D5CCBC] text-black font-semibold text-xs uppercase tracking-wider transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            type="submit">
            {{ isLoading ? 'Envoi...' : 'Envoyer les instructions' }}
          </button>
          <button class="w-full py-3.5 rounded-xl bg-zinc-900/30 text-zinc-400 hover:text-white text-xs uppercase tracking-wider font-medium border border-zinc-800/80 transition-all duration-300" type="button" @click="authMode = 'login'">
            Annuler l'opération
          </button>
        </div>
      </form>

    </div>

    <!-- Address Autocomplete Dropdown (Teleport) -->
    <Teleport to="body">
      <div
        v-if="showAddressSuggestions && addressSuggestions.length > 0 && addressDropdownPosition"
        :style="{
          position: 'fixed',
          top: addressDropdownPosition.top + 'px',
          left: addressDropdownPosition.left + 'px',
          width: addressDropdownPosition.width + 'px',
          zIndex: 99999
        }"
        class="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
        <button
          v-for="(suggestion, index) in addressSuggestions"
          :key="index"
          type="button"
          @click="selectAddress(suggestion)"
          class="w-full px-4 py-3 text-left hover:bg-zinc-800 transition-colors text-sm text-white border-b border-zinc-800 last:border-b-0">
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span class="flex-1">{{ suggestion.description }}</span>
          </div>
        </button>
      </div>
    </Teleport>

    <!-- Prospect Autocomplete Dropdown (Teleport) -->
    <Teleport to="body">
      <div
        v-if="showProspectSuggestions && prospectSuggestions.length > 0 && prospectDropdownPosition"
        :style="{
          position: 'fixed',
          top: prospectDropdownPosition.top + 'px',
          left: prospectDropdownPosition.left + 'px',
          width: prospectDropdownPosition.width + 'px',
          zIndex: 99999
        }"
        class="bg-zinc-900 border border-emerald-500/40 rounded-xl shadow-2xl shadow-emerald-500/10 max-h-80 overflow-y-auto">
        <div class="px-3 py-2 bg-emerald-950/30 border-b border-emerald-900/40 sticky top-0">
          <p class="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
            Établissements trouvés
          </p>
        </div>
        <button
          v-for="(prospect, index) in prospectSuggestions"
          :key="index"
          type="button"
          @click="selectProspect(prospect)"
          class="w-full px-4 py-3 text-left hover:bg-zinc-800 transition-colors text-sm text-white border-b border-zinc-800 last:border-b-0 group">
          <div class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-white group-hover:text-emerald-300 transition-colors truncate">{{ prospect.nom }}</p>
              <p class="text-xs text-zinc-400 mt-0.5 flex items-center gap-1">
                <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="truncate">{{ prospect.adresse }}, {{ prospect.codePostal }} {{ prospect.ville }}</span>
              </p>
              <div v-if="prospect.telephone || prospect.email" class="flex items-center gap-3 mt-1.5">
                <span v-if="prospect.telephone" class="text-[10px] text-zinc-500 flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {{ prospect.telephone }}
                </span>
                <span v-if="prospect.email" class="text-[10px] text-zinc-500 flex items-center gap-1 truncate">
                  <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span class="truncate">{{ prospect.email }}</span>
                </span>
              </div>
            </div>
            <div class="flex items-center justify-center">
              <svg class="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </div>
          </div>
        </button>
      </div>
    </Teleport>

    <!-- Right Side Hero section -->
    <div class="hidden md:block w-1/2 h-full relative bg-zinc-900">
      <div class="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/40 to-transparent z-10 w-1/3"></div>
      <div class="absolute inset-0 bg-zinc-950/10 z-10"></div>

      <div class="absolute bottom-12 left-12 z-20 max-w-xs space-y-2 p-6 bg-zinc-950/40 backdrop-blur-md rounded-2xl border border-zinc-900 text-left">
        <p class="text-xs text-[#D5CCBC] font-serif italic">BookMySalon Pro</p>
        <p class="text-[11px] text-zinc-400 font-light leading-relaxed">"L'infrastructure logicielle qui préserve l'indépendance de votre marque et sanctuarise votre fichier client."</p>
      </div>

      <img
        alt="High-end beauty curation session"
        class="object-cover w-full h-full filter grayscale contrast-105 brightness-[0.85]"
        src="https://img.freepik.com/free-photo/bride-getting-makeup-done-medium-shot_23-2149860778.jpg?uid=R141327891&ga=GA1.1.223045777.1725963888&semt=ais_hybrid&w=740"
      />
    </div>
  </div>
</template>
<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import {  getMonthlyPrice } from '@/config/pricing'

const router = useRouter()

// Auth mode control
const authMode = ref('login')
const passwordVisible = ref(false)
const isLoading = ref(false)

// Stepper control
const currentStep = ref(1)
const totalSteps = 3
const stepLabels = ['Personnel', 'Établissement', 'Configuration']

// Address autocomplete
const addressSuggestions = ref([])
const showAddressSuggestions = ref(false)
const addressDropdownPosition = ref(null)
const addressInput = ref(null)
const addressInputContainer = ref(null)
let addressDebounceTimeout = null

// Prospect autocomplete
const prospectSuggestions = ref([])
const showProspectSuggestions = ref(false)
const prospectDropdownPosition = ref(null)
const prospectInput = ref(null)
const prospectInputContainer = ref(null)
let prospectDebounceTimeout = null
const selectedProspect = ref(null)

// Lifetime tier info
const lifetimeTier = ref(null)

// Referral validation state
const referralValidation = reactive({
  status: '', // '', 'valid', 'invalid'
  message: '',
  checking: false,
  referrerName: ''
})

const loginData = reactive({ email: '', password: '' })
const resetPasswordData = reactive({ email: '' })
const registerData = reactive({
  nom: '',
  prenom: '',
  email: '',
  password: '',
  passwordConfirm: '',
  telephone: '',
  date_naissance: '',
  genre: '',
  nom_societe: '',
  siret: '',
  adresse: '',
  telephone_firm: '',
  email_firm: '',
  sameAsOwnerEmail: false,
  type_compte: 'solo',
  nombre_employes: 1,
  type_tarification: 'flexible',
  montant_abonnement: 14.99,
  acceptCGU: false,
  phoneCountryCode: '+33',
  referralCode: ''
})

// Computed: Plan name based on employees
const selectedPlanName = computed(() => {
  if (registerData.nombre_employes === 1) return 'Solo Studio'
  if (registerData.nombre_employes >= 2 && registerData.nombre_employes <= 3) return 'Équipe Lab'
  if (registerData.nombre_employes >= 4) return 'Salon Plus Cloud'
  return 'Solo Studio'
})

// Computed: Calculated price
const calculatedPrice = computed(() => {
  if (registerData.type_tarification === 'lifetime' && lifetimeTier.value) {
    return lifetimeTier.value.price
  }

  const plan = getMonthlyPrice(registerData.type_compte, registerData.nombre_employes)
  return plan ? plan.price : 14.99
})

// Computed: Password strength
const passwordStrength = computed(() => {
  const pwd = registerData.password

  const hasMinLength = pwd.length >= 8
  const hasUpperCase = /[A-Z]/.test(pwd)
  const hasNumber = /[0-9]/.test(pwd)
const hasSpecialChar = /[!@#$%^&*()_+={}[\]|;':"\\,.<>?/-]/.test(pwd)
  // Calculate level (1-4)
  let level = 0
  if (hasMinLength) level++
  if (hasUpperCase) level++
  if (hasNumber) level++
  if (hasSpecialChar) level++

  return {
    level,
    hasMinLength,
    hasUpperCase,
    hasNumber,
    hasSpecialChar
  }
})

// Watch nombre_employes to update montant_abonnement
watch(() => registerData.nombre_employes, () => {
  updateTypeCompte()
})

// Watch sameAsOwnerEmail to copy email
watch(() => registerData.sameAsOwnerEmail, (newValue) => {
  if (newValue) {
    registerData.email_firm = registerData.email
  }
})

// Watch email to update email_firm if sameAsOwnerEmail is checked
watch(() => registerData.email, (newValue) => {
  if (registerData.sameAsOwnerEmail) {
    registerData.email_firm = newValue
  }
})

// Calculate dropdown position
const updateDropdownPosition = () => {
  if (addressInput.value) {
    const rect = addressInput.value.getBoundingClientRect()
    addressDropdownPosition.value = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width
    }
  }
}

// Address autocomplete functions
const handleAddressInput = () => {
  const query = registerData.adresse

  if (query.length < 3) {
    addressSuggestions.value = []
    showAddressSuggestions.value = false
    return
  }

  // Show suggestions dropdown and update position
  showAddressSuggestions.value = true
  updateDropdownPosition()

  // Debounce pour éviter trop de requêtes
  if (addressDebounceTimeout) {
    clearTimeout(addressDebounceTimeout)
  }

  addressDebounceTimeout = setTimeout(async () => {
    try {
      const response = await fetch(`https://bookmysalon-967a856b16b6.herokuapp.com/api/google-places-autocomplete?input=${encodeURIComponent(query)}`) 
      const data = await response.json()

      if (data.predictions && data.predictions.length > 0) {
        addressSuggestions.value = data.predictions
        showAddressSuggestions.value = true
        updateDropdownPosition()
      } else {
        addressSuggestions.value = []
        showAddressSuggestions.value = false
      }
    } catch (error) {
      console.error('Erreur autocomplete adresse:', error)
      addressSuggestions.value = []
      showAddressSuggestions.value = false
    }
  }, 300)
}

const selectAddress = async (suggestion) => {
  registerData.adresse = suggestion.description
  showAddressSuggestions.value = false
  addressSuggestions.value = []

  // Optionnel : récupérer les détails pour remplir automatiquement d'autres champs
  try {
    const response = await fetch(`/api/google-places-details?place_id=${suggestion.place_id}`)
    const data = await response.json()

    if (data.result) {
      // Vous pouvez extraire le numéro de téléphone, etc.
      // Pour l'instant on garde juste l'adresse
      console.log('Détails du lieu:', data.result)
    }
  } catch (error) {
    console.error('Erreur détails adresse:', error)
  }
}

// Calculate dropdown position for prospects
const updateProspectDropdownPosition = () => {
  if (prospectInput.value) {
    const rect = prospectInput.value.getBoundingClientRect()
    prospectDropdownPosition.value = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width
    }
  }
}

// Prospect autocomplete functions
const handleProspectInput = () => {
  const query = registerData.nom_societe

  // Si l'utilisateur modifie le nom après avoir sélectionné un prospect, réinitialiser
  if (selectedProspect.value && query !== selectedProspect.value.nom) {
    selectedProspect.value = null
  }

  if (query.length < 2) {
    prospectSuggestions.value = []
    showProspectSuggestions.value = false
    return
  }

  // Show suggestions dropdown and update position
  showProspectSuggestions.value = true
  updateProspectDropdownPosition()

  // Debounce pour éviter trop de requêtes
  if (prospectDebounceTimeout) {
    clearTimeout(prospectDebounceTimeout)
  }

  prospectDebounceTimeout = setTimeout(async () => {
    try {
      const response = await api.prospects.searchProspects(query)

      if (response.data.success && response.data.prospects && response.data.prospects.length > 0) {
        prospectSuggestions.value = response.data.prospects
        showProspectSuggestions.value = true
        updateProspectDropdownPosition()
      } else {
        prospectSuggestions.value = []
        showProspectSuggestions.value = false
      }
    } catch (error) {
      console.error('Erreur autocomplete prospect:', error)
      prospectSuggestions.value = []
      showProspectSuggestions.value = false
    }
  }, 300)
}

const selectProspect = (prospect) => {
  // Sauvegarder le prospect sélectionné
  selectedProspect.value = prospect

  // Pré-remplir les champs avec les données du prospect
  registerData.nom_societe = prospect.nom
  registerData.adresse = `${prospect.adresse}, ${prospect.codePostal} ${prospect.ville}`
  registerData.telephone_firm = prospect.telephone || ''
  registerData.email_firm = prospect.email || ''

  // Masquer les suggestions
  showProspectSuggestions.value = false
  prospectSuggestions.value = []

  // Afficher une notification
  toast.success(`Établissement "${prospect.nom}" sélectionné ! Les informations ont été pré-remplies.`, {
    position: 'top-right',
    autoClose: 3000,
    theme: 'dark'
  })
}

// Referral code validation functions
const handleReferralCodeInput = () => {
  // Convert to uppercase
  registerData.referralCode = registerData.referralCode.toUpperCase()

  // Reset validation if empty
  if (registerData.referralCode.length === 0) {
    referralValidation.status = ''
    referralValidation.message = ''
  }
}

const validateReferralCode = async () => {
  // Reset if empty
  if (!registerData.referralCode || registerData.referralCode.trim().length === 0) {
    referralValidation.status = ''
    referralValidation.message = ''
    return
  }

  try {
    referralValidation.checking = true
    const response = await api.auth.validateReferralCode(registerData.referralCode)

    if (response.data.success && response.data.data.valid) {
      referralValidation.status = 'valid'
      referralValidation.message = `Code valide ! Parrainé par ${response.data.data.referrerName}`
      referralValidation.referrerName = response.data.data.referrerName
    }
  } catch (error) {
    referralValidation.status = 'invalid'
    referralValidation.message = error.response?.data?.message || 'Code de parrainage invalide'
    referralValidation.referrerName = ''
  } finally {
    referralValidation.checking = false
  }
}

// Click outside to close suggestions
// Load lifetime tier on mount + Click outside handler
onMounted(async () => {
  // Load lifetime tier
  try {
    const response = await api.auth.getLifetimeTier()
    if (response.data.available) {
      lifetimeTier.value = response.data
    }
  } catch (error) {
    console.error('Erreur chargement palier lifetime:', error)
  }

  // Click outside to close address suggestions
  const handleClickOutside = (e) => {
    const addressInputEl = document.getElementById('adresse')
    const clickedInput = e.target === addressInputEl || addressInputEl?.contains(e.target)

    // Check if clicked on dropdown button
    const clickedDropdownButton = e.target.closest('button[type="button"]')?.textContent?.includes('Rue') ||
                                    e.target.closest('button[type="button"]')?.textContent?.includes('Avenue')

    if (!clickedInput && !clickedDropdownButton) {
      showAddressSuggestions.value = false
    }

    // Close prospect suggestions if clicked outside
    const prospectInputEl = document.getElementById('nom_societe')
    const clickedProspectInput = e.target === prospectInputEl || prospectInputEl?.contains(e.target)
    const clickedProspectDropdown = e.target.closest('button[type="button"]')?.textContent?.includes(registerData.nom_societe)

    if (!clickedProspectInput && !clickedProspectDropdown) {
      showProspectSuggestions.value = false
    }
  }

  document.addEventListener('click', handleClickOutside)

  // Update dropdown position on scroll/resize
  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
  window.addEventListener('scroll', updateProspectDropdownPosition, true)
  window.addEventListener('resize', updateProspectDropdownPosition)
})


// Handle Login
async function handleLogin(event) {
  // Sécurité : Empêche instantanément le rafraîchissement de la page
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  // Anti-double clic / Soumissions simultanées
  if (isLoading.value) return

  try {
    isLoading.value = true

    // Normaliser l'email pour éviter les problèmes backend
    const normalizedEmail = loginData.email.trim().toLowerCase()

    // Appel à l'API d'authentification
    const response = await api.auth.login({
      email: normalizedEmail,
      password: loginData.password
    })
    console.log(response.data)
    // Si le token est présent, la session est valide
    if (response.data.token) {
      // Stockage local des données de session
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.admin || response.data.user))

      // Stocker le type de compte (accountType) pour la navigation
      if (response.data.firm && response.data.firm.type_compte) {
        localStorage.setItem('accountType', response.data.firm.type_compte)
      }

      // Stocker les infos de la firme
      if (response.data.firm) {
        localStorage.setItem('firm', JSON.stringify(response.data.firm))
      }

      // Notification de succès épurée
      toast.success('Connexion réussie !', {
        position: 'top-right',
        autoClose: 2000,
        theme: 'dark'
      })

      // Redirection fluide vers le tableau de bord
      setTimeout(() => {
        router.push('/')
      }, 500)
    }
  } catch (error) {
    console.error('❌ Erreur de connexion:', error)

    // Récupération dynamique du message d'erreur du serveur ou fallback personnalisé
    const errorMessage = error.response?.data?.message || 'Identifiants invalides ou clé d\'accès incorrecte.'

    // Notification d'erreur persistante pour donner le temps de la lecture
    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 5000,
      theme: 'dark'
    })

    // Bloque formellement toute suite d'exécution en cas d'échec
    return false
  } finally {
    // Libère le verrou de chargement dans tous les cas
    isLoading.value = false
  }
}

// Update type_compte based on nombre_employes
function updateTypeCompte() {
  if (registerData.nombre_employes === 1) {
    registerData.type_compte = 'solo'
  } else if (registerData.nombre_employes >= 2 && registerData.nombre_employes <= 3) {
    registerData.type_compte = 'equipe'
  } else {
    registerData.type_compte = 'salon'
  }

  // Update montant based on current selection
  if (registerData.type_tarification === 'flexible') {
    const plan = getMonthlyPrice(registerData.type_compte, registerData.nombre_employes)
    registerData.montant_abonnement = plan ? plan.price : 14.99
  }
}

// Start registration
function startRegistration() {
  authMode.value = 'register'
  currentStep.value = 1

}

// Cancel registration
function cancelRegistration() {
  authMode.value = 'login'
  currentStep.value = 1

  resetRegisterData()
}

// Reset register data
function resetRegisterData() {
  Object.assign(registerData, {
    nom: '',
    prenom: '',
    email: '',
    password: '',
    passwordConfirm: '',
    telephone: '',
    date_naissance: '',
    genre: '',
    nom_societe: '',
    siret: '',
    adresse: '',
    telephone_firm: '',
    email_firm: '',
    type_compte: 'solo',
    nombre_employes: 1,
    type_tarification: 'flexible',
    montant_abonnement: 14.99,
    acceptCGU: false,
    referralCode: ''
  })

  // Reset referral validation
  referralValidation.status = ''
  referralValidation.message = ''
  referralValidation.checking = false
  referralValidation.referrerName = ''
}

// Stepper navigation
function nextStep() {
  if (currentStep.value < totalSteps) {
    if (validateCurrentStep()) {
      currentStep.value++
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// Validate current step
function validateCurrentStep() {
  if (currentStep.value === 1) {
    if (!registerData.nom || !registerData.prenom || !registerData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }

    if (registerData.password !== registerData.passwordConfirm) {
      toast.error('Les mots de passe ne correspondent pas.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }

    // Check password strength
    const strength = passwordStrength.value
    if (!strength.hasMinLength) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }
    if (!strength.hasUpperCase) {
      toast.error('Le mot de passe doit contenir au moins une majuscule.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }
    if (!strength.hasNumber) {
      toast.error('Le mot de passe doit contenir au moins un chiffre.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }
    if (!strength.hasSpecialChar) {
      toast.error('Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*).', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }
  }

  if (currentStep.value === 2) {
    if (!registerData.nom_societe || !registerData.siret || !registerData.adresse || !registerData.telephone_firm) {
      toast.error('Veuillez remplir toutes les informations de l\'établissement.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }

    // Validate email_firm only if not using same email
    if (!registerData.sameAsOwnerEmail && !registerData.email_firm) {
      toast.error('Veuillez remplir l\'email de l\'établissement.', {
        position: 'top-right',
        autoClose: 3000
      })
      return false
    }
  }

  return true
}

// Handle Registration
async function handleRegister() {
  if (isLoading.value) return

  if (!registerData.acceptCGU) {
    toast.error('Vous devez accepter les CGU.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    isLoading.value = true

    // Update montant_abonnement with calculated price
    registerData.montant_abonnement = calculatedPrice.value

    // Normaliser les emails
    const normalizedEmail = registerData.email.trim().toLowerCase()
    const normalizedEmailFirm = registerData.sameAsOwnerEmail
      ? normalizedEmail
      : registerData.email_firm.trim().toLowerCase()

    // Regular registration
    const response = await api.auth.register({
      nom: registerData.nom,
      prenom: registerData.prenom,
      email: normalizedEmail,
      telephone: registerData.telephone || '',
      mot_de_passe: registerData.password,
      nom_societe: registerData.nom_societe,
      siret: registerData.siret,
      adresse: registerData.adresse,
      telephone_firm: registerData.telephone_firm,
      email_firm: normalizedEmailFirm,
      type_compte: registerData.type_compte,
      nombre_employes: registerData.nombre_employes,
      type_tarification: registerData.type_tarification,
      montant_abonnement: registerData.montant_abonnement,
      referralCode: registerData.referralCode || undefined,
      prospectId: selectedProspect.value ? selectedProspect.value._id : undefined
    })

    if (response.data.success && response.data.token) {
      // Sauvegarder le token et les informations utilisateur
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.admin))

      // Stocker le type de compte (accountType) pour la navigation
      if (response.data.firm && response.data.firm.type_compte) {
        localStorage.setItem('accountType', response.data.firm.type_compte)
      }

      // Stocker les infos de la firme
      if (response.data.firm) {
        localStorage.setItem('firm', JSON.stringify(response.data.firm))
      }

      toast.success('Compte créé avec succès ! Bienvenue sur BookMySalon Pro !', {
        position: 'top-right',
        autoClose: 2000
      })

      setTimeout(() => {
        router.push('/')
      }, 1000)
    }
  } catch (error) {
    console.error('Erreur d\'inscription:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la création du compte.', {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isLoading.value = false
  }
}

// Handle Reset Password
async function handleResetPassword() {
  if (isLoading.value) return

  // Validation basique
  if (!resetPasswordData.email || !resetPasswordData.email.includes('@')) {
    toast.error('Veuillez entrer une adresse email valide.', {
      position: 'top-right',
      autoClose: 3000,
      theme: 'dark'
    })
    return
  }

  try {
    isLoading.value = true

    // Normaliser l'email
    const normalizedEmail = resetPasswordData.email.trim().toLowerCase()

    await api.auth.requestPasswordReset(normalizedEmail)

    toast.success('Un email de réinitialisation a été envoyé à votre adresse.', {
      position: 'top-right',
      autoClose: 4000,
      theme: 'dark'
    })

    // Retourner au login après 2 secondes
    setTimeout(() => {
      authMode.value = 'login'
      resetPasswordData.email = ''
    }, 2000)
  } catch (error) {
    console.error('Erreur de réinitialisation:', error)

    // Message d'erreur plus détaillé
    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        'Erreur lors de la demande de réinitialisation. Veuillez réessayer.'

    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 5000,
      theme: 'dark'
    })
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
input[type="date"]::-calendar-picker-indicator {
  filter: invert(0.4) sepia(1) saturate(0) hue-rotate(0deg);
  cursor: pointer;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #27272a; /* zinc-800 */
  border-radius: 9999px;
}
/* Aligne l'UI native du calendrier pour matcher le thème sombre */
.custom-date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
  opacity: 0.5;
}
.custom-date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 0.8;
}
</style>
